import { pick, extend } from 'underscore';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { ERROR_ENTITY, isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { getErrorsPlaceholders } from '@bingads-webui-campaign-react/xandr-campaign';
import { isAudienceShoppingCampaign } from '@bingads-webui-campaign-react/constants';

import { bulkModelToViewModelForAdGroup, viewModelToModelForAdGroup } from './ad-group';
import { PLACEHOLDER_ID } from '../constants';
import {
  viewModelToBulkModelForLocationTargets,
  viewModelToBulkModelForAdSchedules,
  viewModelToBulkModelForAudiences,
} from './ad-group-criterions';
import {
  viewModelToBulkModelForTargeting
} from './campaign-criterions';

const adGroupEDM = edmCampaign.types.resolve('Model.AdGroup');
const adGroupResource = new adGroupEDM.ResourceIdentifier();

const createOrUpdateAdGroup = (data) => {
  const createOrUpdateCall = data && data.Id ?
    adGroupResource.bulkUpdate.bind(adGroupResource) :
    adGroupResource.bulkCreate.bind(adGroupResource);
  return createOrUpdateCall(data);
};

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

const bulkUpsertAdGroupSettingsEntityErrorMap = {
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
};

export const viewModelToBulkModelForAdGroup = ({
  campaignViewModel,
  adGroupViewModel,
  bundleLocationCriterions,
  customTargetingBulkModel = null,
  saveAdGroupCriterions = false,
  updateProductPartitionBids = false,
  isProductGroupAllowedForAudienceCampaign = false,
  i18n,
}) => {
  const isUpdate = !!adGroupViewModel.data.Id;
  const doesCampaignExist = !!campaignViewModel.Id;
  const isAudienceCampaign = isAudienceShoppingCampaign(campaignViewModel.CampaignType, campaignViewModel.CampaignSubType);
  const isAudienceDynamicFeedBasedCampaign = isAudienceCampaign && campaignViewModel.CampaignSubType === 'AudienceDynamicFeedBased';
  // Product groups are always supported for SWF
  // For AudienceFeedBased, it requires an pilot
  // For AudienceDynamicFeedBased, it is not supported
  const isProductGroupSupported = !isAudienceCampaign || (isProductGroupAllowedForAudienceCampaign && !isAudienceDynamicFeedBasedCampaign);
  const adGroupModel = viewModelToModelForAdGroup({ adGroupViewModel, i18n, isAudienceShoppingCampaign: isAudienceCampaign });
  const modelToSave = {
    ...adGroupModel,
    Campaign: doesCampaignExist ? pick(campaignViewModel, 'Id') : {
      Id: PLACEHOLDER_ID.CampaignNegativeReferenceId,
    },
  };

  if (saveAdGroupCriterions) {
    const locationTargetsModel = viewModelToBulkModelForLocationTargets({
      bundleLocationCriterions,
      adGroupViewModel,
      campaignViewModel,
    });
    const adSchedulesModel = viewModelToBulkModelForAdSchedules({ adGroupViewModel, campaignViewModel });
    const targetingModel = customTargetingBulkModel ?
      customTargetingBulkModel({ adGroupViewModel, campaignViewModel }) :
      viewModelToBulkModelForTargeting({
        adGroupViewModel,
        campaignViewModel: {
          data: campaignViewModel,
        },
      });

    const audienceCriterionsModel = viewModelToBulkModelForAudiences({ adGroupViewModel, campaignViewModel });
    modelToSave.AdGroupCriterions = [
      ...locationTargetsModel,
      ...adSchedulesModel,
      ...targetingModel,
    ];
    modelToSave.AdGroupAudienceCriterions = audienceCriterionsModel;
  }

  // Create necessary child entities if this is a new ad group
  if (!isUpdate) {
    // Ads are implicitly created for AWF. Besides, they don't use Product Ads type
    if (!isAudienceCampaign) {
      extend(modelToSave, {
        ProductAds: {
          CampaignName: campaignViewModel.Name,
          AdGroupName: adGroupModel.Name,
          PromotionalText: '',
        },
      });
    }

    if (isProductGroupSupported) {
      extend(modelToSave, {
        ProductPartitions: [{
          BidAmount: adGroupModel.SearchBid,
          ProductCondition: 'All',
          ProductValue: '',
          CampaignName: campaignViewModel.Name,
          AdGroupName: adGroupModel.Name,
          IsExcluded: false,
          IsLeaf: 'Unit',
        }],
      });
    }
  }

  if (updateProductPartitionBids && isProductGroupSupported && adGroupViewModel.data.ProductPartitions) {
    extend(modelToSave, {
      ProductPartitions: adGroupViewModel.data.ProductPartitions.map(productPartition => ({
        ...productPartition,
        BidAmount: adGroupModel.SearchBid,
      })),
    });
  }

  return modelToSave;
};

export const saveAdGroup = async ({
  adGroup: adGroupViewModel,
  bundleCountries,
  bundleLocationCriterions,
  campaign,
  customTargetingBulkModel = null,
  saveAdGroupCriterions = false,
  updateProductPartitionBids = false,
  isProductGroupAllowedForAudienceCampaign = false,
}, {
  scenario, i18n, activityLogger,
}) => {
  const adGroupBulkModel = viewModelToBulkModelForAdGroup({
    campaignViewModel: campaign,
    adGroupViewModel,
    bundleLocationCriterions,
    customTargetingBulkModel,
    updateProductPartitionBids,
    saveAdGroupCriterions,
    isProductGroupAllowedForAudienceCampaign,
    i18n,
  });

  try {
    const result = await createOrUpdateAdGroup(adGroupBulkModel);

    const data = {
      adGroup: bulkModelToViewModelForAdGroup({
        adGroupModel: result,
        originalAdGroupViewModel: adGroupViewModel,
        bundleCountries,
        i18n,
      }),
      ...getErrorsPlaceholders(),
    };
    return data;
  } catch (bulkOrSystemError) {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { entity, errors } = bulkOrSystemError;
      const adGroup = entity ?
        bulkModelToViewModelForAdGroup({
          adGroupModel: entity,
          originalAdGroupViewModel: adGroupViewModel,
          bundleCountries,
          errors,
          i18n,
        }) :
        adGroupViewModel;

      let filteredErrors = errors;
      if (adGroup.data.Id <= 0) {
        filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
      }

      const serverErrors = modelToViewModelForBulkErrors(
        filteredErrors,
        bulkUpsertAdGroupSettingsEntityErrorMap,
        scenario,
        i18n,
        activityLogger
      );

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        adGroup,
        serverErrors,
      });
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`AdGroup bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
    // eslint-disable-next-line prefer-promise-reject-errors

    return {
      adGroup: adGroupViewModel,
      systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
    };
  }
};
